import axios from 'axios';
import { api } from './client';
import type { Difficulty, SerializedBoardDto, TileDto } from './puzzles';
import type { StagedPlacement } from '../game/store';

export interface MoveView {
  move_number: number;
  placements: StagedPlacement[];
  primary_word: string;
  cross_words: Array<{ word: string; score: number }>;
  move_score: number;
  bingo: boolean;
}

export interface LifeState {
  life_id: string;
  life_number: number;
  status: 'IN_PROGRESS' | 'WON' | 'LOST';
  puzzle: {
    id: string;
    puzzle_date: string;
    difficulty: Difficulty;
    move_count: number;
    target_score: number;
  };
  board_state: SerializedBoardDto;
  current_rack: TileDto[];
  bag_remaining: number;
  current_score: number;
  moves_used: number;
  moves_remaining: number;
  moves: MoveView[];
}

export interface SubmitMoveResult {
  life: LifeState;
  move: MoveView;
  replayed: boolean;
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export async function startLife(date: string, difficulty: Difficulty): Promise<LifeState> {
  const res = await api.post(`/api/puzzles/${date}/${difficulty}/lives`, {});
  return res.data;
}

export async function getLife(lifeId: string): Promise<LifeState> {
  const res = await api.get(`/api/lives/${lifeId}`);
  return res.data;
}

// Network-failure retries with backoff (spec 11.1); the idempotency key
// makes re-sending the same move safe.
const RETRY_DELAYS_MS = [1000, 3000, 9000];

export async function submitMove(
  lifeId: string,
  placements: StagedPlacement[],
  claimed_score: number,
  move_idempotency_key: string,
): Promise<SubmitMoveResult> {
  for (let attempt = 0; ; attempt++) {
    try {
      const res = await api.post(`/api/lives/${lifeId}/moves`, {
        placements,
        claimed_score,
        move_idempotency_key,
      });
      return res.data;
    } catch (err) {
      const isNetworkError = axios.isAxiosError(err) && !err.response;
      if (!isNetworkError || attempt >= RETRY_DELAYS_MS.length) throw err;
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
    }
  }
}

export async function finishLife(lifeId: string): Promise<LifeState> {
  const res = await api.post(`/api/lives/${lifeId}/finish`);
  return res.data;
}
